import styled from 'styled-components';
import { FlexAlignCenter, FlexEnd, Headline2 } from 'components/Elements';
import strings from '../../../strings/strings.json';
import { breakpoints, sizes, theme } from 'theme';
import { Organisation } from 'models/Organisation';
import Notification, { NotificationType } from '../../../components/notifiers/Notification';
import Table, { TableRow } from 'components/table/Table';
import { EmptyTableContent } from 'components/table/EmptyTableContent';
import EditIcon from 'assets/icons/controls/EditIcon';
import { useNavigate } from 'react-router-dom';
import PlusIcon from 'assets/icons/controls/PlusIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import OrganisationAdminIcon from 'assets/icons/role-icons/OrganisationAdminIcon';
import useGetRequest from 'hooks/useGetRequest';
import Button from 'components/buttons/Button';

export default function OrganisationList(): JSX.Element {
    const { data: organisations, errors } = useGetRequest<Organisation[]>('organisations');

    const navigate = useNavigate();

    if (errors && errors.length > 0) {
        return (
            <>
                <HeadingContainer>
                    <Headline2>{strings.organisationPage.pageHeading}</Headline2>
                </HeadingContainer>
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.organisationPage.errorsFetchingDataMessage}
                />
            </>
        );
    }

    if (!organisations) return <></>;

    const organisationRows: TableRow[] = organisations
        .sort((a, b) => {
            return a.name > b.name ? 1 : -1;
        })
        .map(({ id, name }) => {
            return {
                id: id,
                cells: [
                    {
                        name: strings.organisationPage.name,
                        value: name,
                        isBold: true,
                    },
                    {
                        name: '',
                        value: (
                            <FlexEnd>
                                <Button
                                    title={strings.organisationPage.editOrganisationButton}
                                    aria={strings.organisationPage.editOrganisationButtonAriaLabel}
                                    onClick={() => navigate(`/admin/organisation/${id}`)}
                                    icon={<EditIcon />}
                                />
                            </FlexEnd>
                        ),
                    },
                ],
            };
        });

    return (
        <div>
            <HeadingContainer>
                <Headline2>{strings.organisationPage.pageHeading}</Headline2>
                {organisationRows.length > 0 && (
                    <Button
                        title={strings.organisationPage.addOrganisationButton}
                        aria={strings.organisationPage.addOrganisationButtonAriaLabel}
                        onClick={() => {
                            navigate('/admin/create-organisation');
                        }}
                        icon={<PlusIcon plusColour={theme.primaryButtonBackgroundColour} size={IconSize.LARGE} />}
                    />
                )}
            </HeadingContainer>
            <Table
                headers={[
                    { name: strings.organisationPage.name, rowPercentage: 50 },
                    { name: '', rowPercentage: 50, mobileRightColumn: true },
                ]}
                rows={organisationRows}
                emptyTableContent={
                    <EmptyTableContent
                        icon={<OrganisationAdminIcon size={IconSize.XLARGE} />}
                        title={strings.organisationPage.emptyUsersTable.title}
                        description={strings.organisationPage.emptyUsersTable.description}
                        buttonTitle={strings.organisationPage.addOrganisationButton}
                        buttonAria={strings.organisationPage.addOrganisationButtonAriaLabel}
                        buttonIcon={
                            <PlusIcon plusColour={theme.primaryButtonBackgroundColour} size={IconSize.LARGE} />
                        }
                        buttonOnClick={() => navigate('/admin/create-organisation')}
                    />
                }
            />
        </div>
    );
}

const HeadingContainer = styled(FlexAlignCenter)`
    justify-content: space-between;
    margin-top: ${sizes.spacingLg};
    margin-bottom: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
    }
`;
